/**
 * Провайдеры Refine.
 *
 * `authProvider` — форма взята из urtDesign, но политика другая: там «авторизация»
 * это наличие Bearer-токена в localStorage, здесь — httpOnly-cookie, которую JS
 * не видит вовсе. Поэтому `check` спрашивает сервер (`/api/me`), а не хранилище:
 * узнать, жива ли сессия, можно только у того, кто её выдал.
 */

import type { AuthProvider, DataProvider } from "@refinedev/core";

import { apiFetch, ApiFailure } from "@/lib/api";

/** Кто вошёл. Приходит из CRM (`sUser`), своей таблицы пользователей нет. */
export type CurrentUser = {
  id: number;
  name: string;
  short: string;
  /** База CRM, из которой кабинет читает числа: `crm-prod` или `crm-dev`. */
  crm?: string;
  crm_is_prod?: boolean;
  /** Коды полномочий prokat (`app/core/modules/auth/permission/registry.py`).
   *  Решает сервер; список нужен, чтобы экран не показывал то, что откажут. */
  permissions: string[];
};

export const authProvider: AuthProvider = {
  check: async () => {
    try {
      await apiFetch<CurrentUser>("/api/me");
      return { authenticated: true };
    } catch {
      return { authenticated: false, redirectTo: "/login", logout: true };
    }
  },

  // Одно поле — пароль: он же идентифицирует человека в CRM. Логина в наших
  // системах не существует, поэтому и в провайдере его нет.
  login: async ({ password }: { password: string }) => {
    try {
      await apiFetch<CurrentUser>("/api/login", {
        method: "POST",
        body: JSON.stringify({ password }),
      });
      // Расширение (`extension/here.js`) переезжает на имя кабинета, где только что вошли.
      document.dispatchEvent(new Event("prokat-login"));
      return { success: true, redirectTo: "/activity" };
    } catch (error) {
      const failure = error instanceof ApiFailure ? error : null;
      return {
        success: false,
        error: {
          name: failure?.code ?? "LOGIN_FAILED",
          message: failure?.message ?? "Не удалось войти",
        },
      };
    }
  },

  logout: async () => {
    // Сессию гасит сервер: без этого строка в базе осталась бы открытой, и
    // украденная cookie продолжала бы работать.
    try {
      await apiFetch<{ ok: boolean }>("/api/logout", { method: "POST" });
    } catch {
      // Даже если сервер не ответил, уводим на форму входа: держать оператора
      // в кабинете, из которого он попросил выйти, — хуже.
    }
    return { success: true, redirectTo: "/login" };
  },

  getIdentity: async () => {
    try {
      return await apiFetch<CurrentUser>("/api/me");
    } catch {
      return null;
    }
  },

  onError: async (error) => {
    // 401 приходит и от истёкшей сессии, и от погашенной администратором.
    // В обоих случаях ответ один: на форму входа, без попытки «починить».
    if (error instanceof ApiFailure && error.status === 401) {
      return { logout: true, redirectTo: "/login", error };
    }
    return { error };
  },
};

/**
 * dataProvider под наш API. Реализован только `custom` (GET): экраны ходят
 * своими запросами; заглушки «на будущее» — код, который никто не проверял.
 */
export const dataProvider: DataProvider = {
  getApiUrl: () => "/api",
  getList: async ({ resource }) => {
    throw new Error(`Ресурс ${resource} не поддерживается`);
  },
  getOne: async ({ resource }) => {
    throw new Error(`Ресурс ${resource} не поддерживается`);
  },
  custom: async ({ url, method }) => {
    if (method !== "get") throw new Error(`Метод ${method} не поддерживается`);
    return { data: (await apiFetch<unknown>(url)) as never };
  },
  create: async () => {
    throw new Error("Не реализовано");
  },
  update: async () => {
    throw new Error("Не реализовано");
  },
  deleteOne: async () => {
    throw new Error("Не реализовано");
  },
};
